import { useState, useCallback, useRef, useMemo } from 'react';

import { Grid, Box, ScrollPanel } from '@simplywallst/ui-core';

import { HoldingTag } from './HoldingTag';
import { EmptyState } from './EmptyState';

import { SearchResult } from '@/components/Search';
import { fullMatchUniqueSymbol } from '@/components/Search/utils';

import { SearchInput } from '../Search';
import { useSearch } from '@/hooks/useSearch';

import { focusAndOpenKeyboard } from '@/utilities/focusAndOpenKeyboard';

interface Props {
  holdings: string[];
  onSelectHolding: (holding: string) => void;
  onRemoveHolding: (holding: string) => void;
}

export const SearchHoldingsMobile = ({
  holdings,
  onRemoveHolding,
  onSelectHolding,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const { data, status } = useSearch(query);

  const matchedUniqueSymbols = useMemo(
    () => fullMatchUniqueSymbol(data),
    [data]
  );

  const handleSelectHolding = useCallback(
    (uniqueSymbol: string) => {
      onSelectHolding(uniqueSymbol);
      setQuery('');
      focusAndOpenKeyboard(inputRef.current, 300);
    },
    [setQuery, onSelectHolding]
  );

  return (
    <Grid templateRows="min-content 1fr">
      <SearchInput
        ref={inputRef}
        value={query}
        status={status}
        onChange={setQuery}
      />
      {(query.length === 0 || data.length === 0) && (
        <Box paddingTop="x2">
          {holdings.map((holding) => {
            return (
              <HoldingTag
                key={holding}
                holding={holding}
                onRemove={() => onRemoveHolding(holding)}
              />
            );
          })}
          {holdings.length < 3 && <EmptyState />}
        </Box>
      )}

      {query.length > 0 && data.length > 0 && status === 'success' && (
        <Box position="relative">
          <ScrollPanel
            overflow="hidden auto"
            width="100%"
            height="100%"
            position="absolute"
          >
            {data.map((result, i) => (
              <SearchResult
                key={result.objectID}
                fullMatchUniqueSymbol={matchedUniqueSymbols}
                id={result.objectID}
                listIndex={i}
                listings={result.listings}
                name={result.name}
                uniqueSymbol={result.uniqueSymbol}
                canonicalUrl={result.url}
                market={result.exchangeCountryIso}
                tag={result.tag}
                primaryWrapper={(child) => (
                  <div
                    onClick={() => {
                      handleSelectHolding(result.uniqueSymbol);
                    }}
                  >
                    {child}
                  </div>
                )}
                secondaryWrapper={(child, listing) => (
                  <div
                    onClick={() => {
                      handleSelectHolding(listing.uniqueSymbol);
                    }}
                  >
                    {child}
                  </div>
                )}
                type={result.type}
                description={result.description}
                imageUrl={result.imageUrl}
                website={result.website}
                tickerSymbol={result.tickerSymbol}
              />
            ))}
          </ScrollPanel>
        </Box>
      )}
    </Grid>
  );
};
